import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  ShieldCheck,
  ArrowLeft,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  User,
  Calendar,
  FileText,
  History,
} from 'lucide-react'
import { useAuthStore } from '@personic/shared'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001'

interface Attestation {
  id: string
  tenant_id: string
  framework: string
  status: string
  attested_by_id: string | null
  attested_by_name: string
  attested_by_title: string
  attested_by_email: string | null
  signature_type: string
  attestation_data: {
    acknowledgments: Record<string, boolean>
    submittedAt?: string
  }
  ip_address: string
  submitted_at: string
  reviewed_at: string | null
  reviewed_by_id: string | null
  review_notes: string | null
  expires_at: string
  is_expired: boolean
  is_active: boolean
  days_until_expiry: number
  created_at: string
  updated_at: string | null
}

interface Acknowledgment {
  id: string
  user_id: string
  action: string
  context: string
  decision: string
  acknowledged_at: string
}

// Framework display info
const FRAMEWORK_INFO: Record<
  string,
  {
    name: string
    fullName: string
    product: string
    acknowledgmentLabels: Record<string, string>
  }
> = {
  hipaa: {
    name: 'HIPAA',
    fullName: 'Health Insurance Portability and Accountability Act',
    product: 'Clinical',
    acknowledgmentLabels: {
      covered_entity: 'Covered Entity certification',
      baa_commitment: 'Business Associate Agreement acknowledgment',
      compliance_program: 'HIPAA compliance program attestation',
      minimum_necessary: 'Minimum Necessary standard commitment',
    },
  },
  eeoc: {
    name: 'EEOC',
    fullName: 'Equal Employment Opportunity Commission',
    product: 'Talent',
    acknowledgmentLabels: {
      fair_hiring: 'Fair hiring practices commitment',
      adverse_impact: 'Adverse impact monitoring agreement',
      non_discrimination: 'Non-discrimination policy acknowledgment',
      human_decision: 'Human-in-loop decision commitment',
    },
  },
  batson: {
    name: 'Batson',
    fullName: 'Batson v. Kentucky Compliance',
    product: 'Legal',
    acknowledgmentLabels: {
      methodology_acknowledgment: 'Methodology acknowledgment',
      human_in_loop: 'Human-in-loop commitment',
      expert_review: 'Expert review agreement',
      non_discriminatory: 'Non-discrimination certification',
    },
  },
  ferpa: {
    name: 'FERPA',
    fullName: 'Family Educational Rights and Privacy Act',
    product: 'Education',
    acknowledgmentLabels: {
      educational_institution: 'Educational institution certification',
      student_data_protection: 'Student data protection commitment',
      parental_consent: 'Parental consent acknowledgment',
      legitimate_educational: 'Legitimate educational purpose certification',
    },
  },
  finra: {
    name: 'FINRA',
    fullName: 'Financial Industry Regulatory Authority',
    product: 'Finance',
    acknowledgmentLabels: {
      registered_firm: 'Registered firm certification',
      suitability: 'Suitability documentation commitment',
      compliance_program: 'Compliance program acknowledgment',
      disclosure: 'Disclosure commitment',
    },
  },
}

export default function AttestationDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { accessToken } = useAuthStore()
  const [attestation, setAttestation] = useState<Attestation | null>(null)
  const [acknowledgments, setAcknowledgments] = useState<Acknowledgment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAttestation = async () => {
    setLoading(true)
    setError(null)

    try {
      // Fetch attestation details
      const response = await fetch(`${API_BASE_URL}/attestations/${id}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch attestation')
      }

      const data = await response.json()
      setAttestation(data)

      // Fetch acknowledgments
      const acksResponse = await fetch(
        `${API_BASE_URL}/attestations/${id}/acknowledgments`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )

      if (acksResponse.ok) {
        const acksData = await acksResponse.json()
        setAcknowledgments(acksData)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (id) {
      fetchAttestation()
    }
  }, [id, accessToken])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    )
  }

  if (error || !attestation) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="p-6 bg-red-50 border border-red-200 rounded-lg text-center">
          <p className="text-red-700">{error || 'Attestation not found'}</p>
          <button
            onClick={() => navigate('/tenant/attestations')}
            className="mt-4 text-red-600 hover:text-red-800 underline"
          >
            Back to Attestations
          </button>
        </div>
      </div>
    )
  }

  const info = FRAMEWORK_INFO[attestation.framework]

  const getStatusBadge = () => {
    if (attestation.is_expired) {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-sm font-medium">
          <XCircle className="w-4 h-4" />
          Expired
        </span>
      )
    }

    switch (attestation.status) {
      case 'approved':
        return attestation.days_until_expiry <= 30 ? (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
            <AlertTriangle className="w-4 h-4" />
            Expiring Soon ({attestation.days_until_expiry} days)
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
            <CheckCircle className="w-4 h-4" />
            Active
          </span>
        )
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
            <Clock className="w-4 h-4" />
            Pending Review
          </span>
        )
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-sm font-medium">
            <XCircle className="w-4 h-4" />
            Rejected
          </span>
        )
      default:
        return null
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back Link */}
      <Link
        to="/tenant/attestations"
        className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Attestations
      </Link>

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-personic-100 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-personic-600" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl font-bold text-gray-900">
                  {info?.name || attestation.framework.toUpperCase()} Attestation
                </h1>
                <span className="text-sm text-gray-500 px-2 py-0.5 bg-gray-100 rounded">
                  {info?.product}
                </span>
              </div>
              <p className="text-gray-500">{info?.fullName}</p>
            </div>
          </div>
          {getStatusBadge()}
        </div>

        {/* Renewal Action */}
        {attestation.status === 'approved' &&
          !attestation.is_expired &&
          attestation.days_until_expiry <= 30 && (
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-amber-800">
                    Renewal Required
                  </h3>
                  <p className="text-sm text-amber-700 mt-1">
                    This attestation expires in {attestation.days_until_expiry}{' '}
                    days. Submit a new attestation to maintain access.
                  </p>
                </div>
                <Link
                  to="/tenant/attestations/new"
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
                >
                  Renew Now
                </Link>
              </div>
            </div>
          )}

        {/* Rejected Message */}
        {attestation.status === 'rejected' && attestation.review_notes && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <h3 className="font-medium text-red-800 mb-2">Rejection Reason</h3>
            <p className="text-sm text-red-700">{attestation.review_notes}</p>
          </div>
        )}
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Attestor Information */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
            <User className="w-5 h-5 text-gray-400" />
            Authorized Representative
          </h2>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm text-gray-500">Name</dt>
              <dd className="text-gray-900">{attestation.attested_by_name}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Title</dt>
              <dd className="text-gray-900">{attestation.attested_by_title}</dd>
            </div>
            {attestation.attested_by_email && (
              <div>
                <dt className="text-sm text-gray-500">Email</dt>
                <dd className="text-gray-900">{attestation.attested_by_email}</dd>
              </div>
            )}
            <div>
              <dt className="text-sm text-gray-500">Signature Type</dt>
              <dd className="text-gray-900 capitalize">
                {attestation.signature_type}
              </dd>
            </div>
          </dl>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
            <Calendar className="w-5 h-5 text-gray-400" />
            Timeline
          </h2>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm text-gray-500">Submitted</dt>
              <dd className="text-gray-900">
                {new Date(attestation.submitted_at).toLocaleString()}
              </dd>
            </div>
            {attestation.reviewed_at && (
              <div>
                <dt className="text-sm text-gray-500">Reviewed</dt>
                <dd className="text-gray-900">
                  {new Date(attestation.reviewed_at).toLocaleString()}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-sm text-gray-500">Expires</dt>
              <dd className="text-gray-900">
                {new Date(attestation.expires_at).toLocaleString()}
              </dd>
            </div>
            {!attestation.is_expired && attestation.status === 'approved' && (
              <div>
                <dt className="text-sm text-gray-500">Days Until Expiry</dt>
                <dd className="text-gray-900">{attestation.days_until_expiry}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Acknowledgments */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
          <FileText className="w-5 h-5 text-gray-400" />
          Acknowledgments
        </h2>
        <ul className="space-y-3">
          {Object.entries(attestation.attestation_data.acknowledgments || {}).map(
            ([key, value]) => (
              <li key={key} className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    value
                      ? 'bg-emerald-100 text-emerald-600'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  <CheckCircle className="w-3.5 h-3.5" />
                </div>
                <span className="text-gray-700">
                  {info?.acknowledgmentLabels[key] || key}
                </span>
              </li>
            )
          )}
        </ul>
      </div>

      {/* Acknowledgment Log */}
      {acknowledgments.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
            <History className="w-5 h-5 text-gray-400" />
            Acknowledgment Log
          </h2>
          <div className="space-y-3">
            {acknowledgments.map((ack) => (
              <div
                key={ack.id}
                className="p-3 bg-gray-50 rounded-lg border border-gray-100"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900">
                    {ack.action}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(ack.acknowledged_at).toLocaleString()}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{ack.context}</p>
                <span
                  className={`inline-block mt-2 text-xs px-2 py-0.5 rounded ${
                    ack.decision === 'acknowledged'
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {ack.decision}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Audit Info */}
      <div className="mt-6 text-xs text-gray-400">
        <p>IP Address: {attestation.ip_address}</p>
        <p>Attestation ID: {attestation.id}</p>
      </div>
    </div>
  )
}
